import React, { useState, useEffect, useRef } from 'react';
import { FaVideo, FaPhoneSlash, FaPhone, FaUserCircle } from 'react-icons/fa';
import ringtone from '../../utils/ringtoneHelper';
import { showNotification } from '../../utils/notificationHelper';
import './GlobalIncomingCallModal.css';

const GlobalIncomingCallModal = ({ callData, onAccept, onReject }) => {
    const [countdown, setCountdown] = useState(60);
    const [isVisible, setIsVisible] = useState(false);

    const actionTimer = useRef(null);
    const handled = useRef(false);
    const callbacks = useRef({ onAccept, onReject });
    callbacks.current = { onAccept, onReject };

    const finish = (action) => {
        if (handled.current) return;
        handled.current = true;
        ringtone.stop();
        setIsVisible(false);
        const meetingId = callData.meeting_id;
        actionTimer.current = setTimeout(() => callbacks.current[action](meetingId), 300);
    };

    useEffect(() => {
        handled.current = false;
        if (!callData) {
            ringtone.stop();
            return;
        }
        setIsVisible(true);
        // Notification support is optional: its failure must never abort cleanup registration.
        const notification = showNotification('Incoming Call', {
            body: `${callData.host?.name || 'Someone'} is calling you`,
            icon: callData.host?.avatar || '/default-avatar.png',
            tag: `call-${callData.meeting_id}`,
            requireInteraction: true,
        });
        if (notification) notification.onclick = () => { window.focus(); notification.close(); };
        try { ringtone.play(); } catch (error) { console.warn('Ringtone unavailable', error); ringtone.stop(); }
        const suppliedExpiry = Date.parse(callData.expires_at);
        const deadline = Number.isFinite(suppliedExpiry) ? suppliedExpiry : Date.now() + 60000;
        const tick = () => {
            const remaining = Math.max(0, Math.ceil((deadline - Date.now()) / 1000));
            setCountdown(remaining);
            if (remaining === 0 && !handled.current) finish('onReject');
        };
        tick();
        const timer = setInterval(tick, 1000);
        return () => {
            clearInterval(timer);
            clearTimeout(actionTimer.current);
            ringtone.stop();
            notification?.close();
        };
        // Callback identity changes must not restart ringing or reset expiry.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [callData?.meeting_id, callData?.expires_at]);

    const handleAccept = () => finish('onAccept');
    const handleReject = () => finish('onReject');

    if (!callData) return null;

    const { host, title, meeting_mode, call_type } = callData;
    const isVoiceCall = call_type === 'voice';

    return (
        <div className={`global-incoming-call-overlay ${isVisible ? 'visible' : ''}`}>
            <div className={`global-incoming-call-modal ${isVisible ? 'visible' : ''}`}>
                <div className="global-call-icon-ring">
                    {isVoiceCall ? (
                        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="global-call-icon">
                            <path d="M20.8472 14.8554L16.4306 12.8764L16.4184 12.8707C16.1892 12.7727 15.939 12.7333 15.6907 12.7562C15.4424 12.7792 15.2037 12.8636 14.9963 13.002C14.9718 13.0181 14.9484 13.0357 14.9259 13.0545L12.6441 14.9998C11.1984 14.2976 9.70595 12.8164 9.00376 11.3895L10.9519 9.07294C10.9706 9.0495 10.9884 9.02606 11.0053 9.00075C11.1407 8.79384 11.2229 8.55667 11.2445 8.31035C11.2661 8.06402 11.2264 7.81618 11.1291 7.58887V7.57762L9.14438 3.15356C9.0157 2.85662 8.79444 2.60926 8.51362 2.44841C8.2328 2.28756 7.9075 2.22184 7.58626 2.26106C6.31592 2.42822 5.14986 3.05209 4.30588 4.01615C3.4619 4.98021 2.99771 6.21852 3.00001 7.49981C3.00001 14.9436 9.05626 20.9998 16.5 20.9998C17.7813 21.0021 19.0196 20.5379 19.9837 19.6939C20.9477 18.85 21.5716 17.6839 21.7388 16.4136C21.7781 16.0924 21.7125 15.7672 21.5518 15.4864C21.3911 15.2056 21.144 14.9843 20.8472 14.8554ZM16.5 19.4998C13.3185 19.4963 10.2682 18.2309 8.01856 15.9813C5.76888 13.7316 4.50348 10.6813 4.50001 7.49981C4.49648 6.58433 4.82631 5.69887 5.42789 5.00879C6.02947 4.3187 6.86167 3.87118 7.76907 3.74981C7.7687 3.75355 7.7687 3.75732 7.76907 3.76106L9.73782 8.16731L7.80001 10.4867C7.78034 10.5093 7.76247 10.5335 7.74657 10.5589C7.60549 10.7754 7.52273 11.0246 7.5063 11.2825C7.48988 11.5404 7.54035 11.7981 7.65282 12.0307C8.5022 13.7679 10.2525 15.5051 12.0084 16.3536C12.2428 16.465 12.502 16.5137 12.7608 16.495C13.0196 16.4762 13.2692 16.3907 13.485 16.2467C13.5091 16.2305 13.5322 16.2129 13.5544 16.1942L15.8334 14.2498L20.2397 16.2232C20.2397 16.2232 20.2472 16.2232 20.25 16.2232C20.1301 17.1319 19.6833 17.9658 18.9931 18.5689C18.3028 19.172 17.4166 19.5029 16.5 19.4998Z" fill="currentColor"/>
                        </svg>
                    ) : (
                        <FaVideo className="global-call-icon" />
                    )}
                </div>

                <div className="global-caller-info">
                    {host?.avatar ? (
                        <img src={host.avatar} alt={host.name} className="global-caller-avatar" />
                    ) : (
                        <div className="global-caller-avatar-placeholder">
                            <FaUserCircle size={80} />
                        </div>
                    )}
                    <h2 className="global-caller-name">{host?.name || 'Unknown'} is calling you</h2>
                    <p className="global-meeting-title">{title}</p>
                    <span className="global-meeting-type-badge">
                        {isVoiceCall ? 'Voice Call' : (meeting_mode === 'one_to_one' ? 'Video Call' : 'Group Video Call')}
                    </span>
                </div>

                <div className="global-call-actions">
                    <button
                        className="global-call-action-btn accept-btn"
                        onClick={handleAccept}
                        title="Accept call"
                    >
                        <FaPhone />
                        <span>Accept</span>
                    </button>
                    <button
                        className="global-call-action-btn reject-btn"
                        onClick={handleReject}
                        title="Reject call"
                    >
                        <FaPhoneSlash />
                        <span>Reject</span>
                    </button>
                </div>

                <div className="global-auto-dismiss-timer">
                    Auto-dismiss in {countdown}s
                </div>
            </div>
        </div>
    );
};

export default GlobalIncomingCallModal;
